import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { FileInput, FileUploader } from "./base";

interface ControlledFileInputProps extends React.ComponentProps<
  typeof FileInput
> {
  info?: string;
  errorName?: string;
  rules?: {
    required?: { value: boolean; message: string };
    validate?: (val: unknown) => string | undefined;
  };
  existingFileUrl?: string | null;
  onDeleteExisting?: () => void;
  uploader?: FileUploader;
}

export function ControlledFileInput({
  name,
  label = "",
  info,
  errorName = "",
  rules = {},
  disabled = false,
  multiple = false,
  accept,
  placeholder,
  hint,
  compact,
  maxSize,
  maxFiles,
  existingFileUrl,
  onDeleteExisting,
  uploader,
  onUploadingChange,
}: ControlledFileInputProps) {
  const { control, getFieldState } = useFormContext();
  const { field, fieldState } = useController({ name, control, rules });
  const required = rules.required?.value === true;

  const { error } = useMemo(
    () => getFieldState(errorName ? errorName : name),
    [getFieldState, errorName, name, fieldState.error],
  );

  if (uploader) {
    return (
      <FileInput
        name={field.name}
        label={label}
        info={info}
        error={error?.message}
        required={required}
        disabled={field.disabled || disabled}
        multiple={multiple}
        accept={accept}
        placeholder={placeholder}
        hint={hint}
        compact={compact}
        maxSize={maxSize}
        maxFiles={maxFiles}
        uploader={uploader}
        onUploadedChange={(codes) =>
          field.onChange(multiple ? codes : (codes[0] ?? null))
        }
        onUploadingChange={onUploadingChange}
        refCallback={field.ref}
      />
    );
  }

  return (
    <FileInput
      name={field.name}
      label={label}
      info={info}
      error={error?.message}
      required={required}
      disabled={field.disabled || disabled}
      multiple={multiple}
      accept={accept}
      value={field.value}
      onChange={field.onChange}
      refCallback={field.ref}
      placeholder={placeholder}
      hint={hint}
      compact={compact}
      maxSize={maxSize}
      maxFiles={maxFiles}
      existingFileUrl={existingFileUrl}
      onDeleteExisting={onDeleteExisting}
    />
  );
}
